<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import FileUpload from 'primevue/fileupload';

// Props
interface Props {
    visible: boolean;
    selectedProduct?: string;
    hasDefaultTexts?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    hasDefaultTexts: false,
});

// Emits
interface Emits {
    (e: 'update:visible', visible: boolean): void;
    (e: 'import', data: { file: File; importType: 'profile' | 'questionnaire' | 'texts' }): void;
}

const emit = defineEmits<Emits>();

// Composables
const { t } = useI18n();

// State
const selectedImportType = ref<'profile' | 'questionnaire' | 'texts'>(
    props.hasDefaultTexts ? 'texts' : 'profile'
);
const selectedFile = ref<File | null>(null);
const importing = ref(false);

// Options for import type selection
const importTypeOptions = computed(() => {
    if (props.hasDefaultTexts) {
        // For standalone products, only show texts option
        return [
            { label: t('textMaintenance.csvImport.texts'), value: 'texts' }
        ];
    } else {
        // For profile/questionnaire products, show all options including texts
        return [
            { label: t('textMaintenance.csvImport.profile'), value: 'profile' },
            { label: t('textMaintenance.csvImport.questionnaire'), value: 'questionnaire' },
            { label: t('textMaintenance.csvImport.texts'), value: 'texts' },
        ];
    }
});

// Computed
const canImport = computed(() => {
    return selectedImportType.value && selectedFile.value;
});

// Methods
const handleVisibilityChange = (visible: boolean) => {
    emit('update:visible', visible);
    if (!visible) {
        resetDialog();
    }
};

const onFileSelect = (event: any) => {
    const files = event.files;
    if (files && files.length > 0) {
        selectedFile.value = files[0];
    }
};

const performImport = () => {
    if (!canImport.value) return;
    
    importing.value = true;
    emit('import', {
        file: selectedFile.value!,
        importType: selectedImportType.value
    });
};

const cancel = () => {
    emit('update:visible', false);
};

const resetDialog = () => {
    selectedImportType.value = props.hasDefaultTexts ? 'texts' : 'profile';
    selectedFile.value = null;
    importing.value = false;
};

// Watch for external changes to stop loading state
watch(() => props.visible, (newVisible) => {
    if (!newVisible) {
        importing.value = false;
    }
});

// Expose methods for parent component
defineExpose({
    resetDialog,
    setImporting: (value: boolean) => {
        importing.value = value;
    }
});
</script>

<template>
    <Dialog 
        :visible="visible" 
        :header="t('textMaintenance.csvImport.title')"
        :modal="true"
        :closable="true"
        :draggable="false"
        :style="{ width: '500px' }"
        @update:visible="handleVisibilityChange"
        data-cy="text-maintenance_import-dialog"
    >
        <div class="flex flex-col gap-6">
            <!-- Product Information -->
            <div v-if="selectedProduct" class="p-4">
                <div class="flex items-center gap-2">
                    <i class="pi pi-info-circle"></i>
                    <span>
                        {{ t('textMaintenance.csvImport.importingTo') }}: <strong>{{ selectedProduct }}</strong>
                    </span>
                </div>
            </div>

            <!-- Import Type Selection -->
            <div>
                <label for="importTypeSelect" class="block text-sm font-medium mb-2">
                    {{ t('textMaintenance.csvImport.selectType') }}
                </label>
                <Select
                    id="importTypeSelect"
                    v-model="selectedImportType"
                    :options="importTypeOptions"
                    optionLabel="label"
                    optionValue="value"
                    class="w-full"
                    data-cy="text-maintenance_import-type-select"
                />
            </div>

            <!-- File Selection -->
            <div>
                <label for="fileUpload" class="block text-sm font-medium mb-2">
                    {{ t('textMaintenance.csvImport.selectFile') }}
                </label>
                <FileUpload
                    id="fileUpload"
                    mode="basic"
                    :choose-label="t('textMaintenance.csvImport.chooseFile')"
                    accept=".csv"
                    :max-file-size="10000000"
                    :custom-upload="true"
                    @select="onFileSelect"
                    class="w-full"
                    data-cy="text-maintenance_import-file-upload"
                />
            </div>
        </div>

        <template #footer>
            <div class="flex justify-end gap-2">
                <Button 
                    :label="t('buttons.cancel')" 
                    severity="secondary"
                    @click="cancel"
                    data-cy="text-maintenance_import-cancel-button"
                />
                <Button 
                    :label="t('textMaintenance.csvImport.import')" 
                    icon="pi pi-upload" 
                    @click="performImport"
                    :disabled="!canImport"
                    :loading="importing"
                    data-cy="text-maintenance_import-confirm-button"
                />
            </div>
        </template>
    </Dialog>
</template>

